import _ from 'lodash';

import { guessBulletOptions } from './buildBulletSeries';
import { withoutUndefined } from './canvasMapping/dateSettings';
import { toSeriesDataKey } from './seriesVariants';
import { isTimelineHeatmap } from './visTypes';

// How a chart draws what it shows: data labels, stacking and how each metric
// is drawn (see Studio.ChartOptions). Intervals, sorting and limits are in
// chartOptions.ts.

type AxisRangeScope =
  // Every metric has its own axis (time series)
  | 'every'
  // Everything is drawn on the first metric's axis (bar charts)
  | 'first'
  // The first metric's axis and those of metrics with their own (combo)
  | 'owners';

export interface DisplayCapabilities {
  // Every chart but a Table can hide its legend
  canHideLegend: boolean;
  canShowDataLabels: boolean;
  // Bar charts can label every Nth bar; tree maps and waterfalls label all
  canStepLabels: boolean;
  // Stacked Bar takes a direction, percentage and pivot; Area a pivot
  stack?: 'bar' | 'area';
  canLabelMetrics: boolean;
  isCombo: boolean;
  canShowAverageAndTrend: boolean;
  axisRange?: AxisRangeScope;
  // Straight, smooth or stepped lines (see Studio.ChartStyle)
  canCurve: boolean;
  // The fills a line can take: Line solid or gradient, Area (always filled)
  // a gradient
  lineFills: NonNullable<Studio.ChartStyle['lineFill']>[];
  // A background track on bars (a Pictorial Bar's is faint icons)
  canStyleBars: boolean;
  // Bars that fade towards their base: not a Pictorial Bar's icons
  canFadeBars: boolean;
  // A Pictorial Bar's icon (see Studio.PictorialOptions)
  isPictorial: boolean;
  // A Map's area and look (see Studio.MapOptions)
  isMap: boolean;
  // A Timeline Heatmap's rows can be coloured in bands between thresholds
  canColourByThresholds: boolean;
  // A Bullet's bands, target labels and status values (see
  // BulletCharts.DisplayOptions)
  isBullet: boolean;
}

// A Pictorial Bar's icons are picked per series key; drawn, they're looked
// up by each series' data key (see toSeriesDataKey)
const withDataKeyedIcons = ({
  pictorial,
  content,
}: {
  pictorial: Studio.PictorialOptions;
  content: Partial<Pick<Studio.ChartContent, 'id' | 'seriesVariants'>>;
}): Studio.PictorialOptions => {
  const { id } = content;
  if (!pictorial.metricIcons || !id || !content.seriesVariants) {
    return pictorial;
  }
  return {
    ...pictorial,
    metricIcons: _.mapKeys(pictorial.metricIcons, (_icon, key) =>
      toSeriesDataKey({
        card: { id, seriesVariants: content.seriesVariants },
        key,
      }),
    ),
  };
};

// The chart's style settings, where its vis type takes them
export const getChartStyle = (
  content: Pick<Studio.ChartContent, 'visType' | 'chart'> &
    Partial<Pick<Studio.ChartContent, 'id' | 'seriesVariants'>>,
): Studio.ChartStyle => {
  const options = content.chart ?? {};
  const display = getDisplayCapabilities(content);
  return withoutUndefined({
    curve: display.canCurve ? options.curve : undefined,
    lineFill:
      options.lineFill && display.lineFills.includes(options.lineFill)
        ? options.lineFill
        : undefined,
    hasBarBackground:
      (display.canStyleBars && options.hasBarBackground) || undefined,
    isBarGradient: (display.canFadeBars && options.isBarGradient) || undefined,
    // Drawn with icons even before one's picked
    pictorial: display.isPictorial
      ? withDataKeyedIcons({ pictorial: options.pictorial ?? {}, content })
      : undefined,
  });
};

export interface MetricCapabilities {
  canLabel: boolean;
  canSetSeriesType: boolean;
  canHaveOwnAxis: boolean;
  canSetAxisSide: boolean;
  canShowAverageAndTrend: boolean;
  canSetAxisRange: boolean;
  canColourByThresholds: boolean;
}

// Following what each renderer reads: bar charts (toBarSeries, useBarData)
// draw labels, averages and trends; time series give each metric an axis
export const getDisplayCapabilities = (
  content: Pick<Studio.ChartContent, 'visType'> &
    Partial<Pick<Studio.ChartContent, 'chart'>>,
): DisplayCapabilities => {
  const { visType } = content;
  // A Pictorial Bar is a Bar drawn with icons
  const isBarChart = visType === 'bar' || visType === 'pictorialBar';
  const isCombo = visType === 'combo';
  const axisRange = ((): AxisRangeScope | undefined => {
    // A bullet per metric, each on its own scale
    if (visType === 'line' || visType === 'bullet') {
      return 'every';
    }
    if (isBarChart) {
      return 'first';
    }
    return isCombo ? 'owners' : undefined;
  })();
  return {
    // Tables, trend cards, gauges and bullets draw no legend
    canHideLegend:
      visType !== 'table' &&
      visType !== 'gauge' &&
      visType !== 'trend' &&
      visType !== 'bullet',
    canShowDataLabels:
      isBarChart || isCombo || visType === 'treeMap' || visType === 'waterfall',
    canStepLabels: isBarChart || isCombo,
    stack:
      visType === 'stackedBar'
        ? 'bar'
        : visType === 'area'
          ? 'area'
          : undefined,
    // A waterfall's name shows in its tooltip
    canLabelMetrics:
      visType === 'line' ||
      isBarChart ||
      isCombo ||
      visType === 'radar' ||
      visType === 'scatter' ||
      visType === 'waterfall' ||
      visType === 'bullet',
    isCombo,
    canShowAverageAndTrend: isBarChart || isCombo,
    axisRange,
    canCurve: visType === 'line' || visType === 'area' || isCombo,
    lineFills:
      visType === 'line'
        ? ['solid', 'gradient']
        : visType === 'area'
          ? ['gradient']
          : [],
    canStyleBars: isBarChart,
    canFadeBars: visType === 'bar',
    isPictorial: visType === 'pictorialBar',
    isMap: visType === 'regionMap',
    canColourByThresholds: isTimelineHeatmap(content),
    isBullet: visType === 'bullet',
  };
};

// The most metrics a chart draws: scatter takes x and y, bubble a size too,
// and pies, stacked charts, heatmaps and the like only their first (a
// Timeline heatmap all of them, a row each). None for charts that draw every
// metric, or a Number (the rest are for its subtext).
export const getMetricLimit = (
  content: Pick<Studio.ChartContent, 'visType' | 'metricIds'> &
    Partial<Pick<Studio.ChartContent, 'chart'>>,
): number | undefined => {
  if (isTimelineHeatmap(content)) {
    return undefined;
  }
  switch (content.visType) {
    case 'scatter':
      // x, y and size
      return 3;
    case 'pie':
    case 'stackedBar':
    case 'area':
    case 'heatmap':
    case 'treeMap':
    case 'waterfall':
    case 'packedBubble':
    case 'funnel':
    case 'sankey':
    case 'regionMap':
    case 'trend':
    case 'gauge':
      return 1;
    default:
      return undefined;
  }
};

// The metrics a chart draws (see getMetricLimit). Only these have drawing
// options.
export const getDrawnMetricIds = (
  content: Pick<Studio.ChartContent, 'visType' | 'metricIds'> &
    Partial<Pick<Studio.ChartContent, 'chart'>>,
): string[] => {
  const metricIds = content.metricIds ?? [];
  const limit = getMetricLimit(content);
  return limit === undefined ? metricIds : metricIds.slice(0, limit);
};

// Combo draws its first metric as columns and the rest as lines
export const getDefaultSeriesType = (
  index: number,
): NonNullable<Studio.ChartMetricOptions['seriesType']> =>
  index === 0 ? 'column' : 'line';

export const getMetricCapabilities = ({
  display,
  metricOptions,
  index,
}: {
  display: DisplayCapabilities;
  metricOptions: Studio.ChartMetricOptions;
  index: number;
}): MetricCapabilities => {
  const canHaveOwnAxis = display.isCombo && index > 0;
  const isAxisOwner =
    index === 0 || (canHaveOwnAxis && !!metricOptions.hasOwnAxis);
  return {
    canLabel: display.canLabelMetrics,
    canSetSeriesType: display.isCombo,
    canHaveOwnAxis,
    canSetAxisSide: display.isCombo && isAxisOwner,
    canShowAverageAndTrend: display.canShowAverageAndTrend,
    canSetAxisRange:
      display.axisRange === 'every' ||
      (display.axisRange === 'first' && index === 0) ||
      (display.axisRange === 'owners' && isAxisOwner),
    canColourByThresholds: display.canColourByThresholds,
  };
};

// A series drawn as its metric's options say, where the chart can. Each
// setting is only written when chosen, so charts saved without options
// rebuild exactly.
export const withSeriesDisplay = ({
  series,
  seriesKey,
  index,
  options,
  display,
}: {
  series: V5ChartDefinitionSeries;
  // The series' entry in the block's metricIds, its options' key (a
  // variant's own, see seriesVariants); absent is its metric
  seriesKey?: string;
  index: number;
  options: Studio.ChartOptions;
  display: DisplayCapabilities;
}): V5ChartDefinitionSeries => {
  const m = (options.metrics ?? {})[seriesKey ?? series.metricId] ?? {};
  const can = getMetricCapabilities({ display, metricOptions: m, index });
  const range = can.canSetAxisRange ? m : {};
  return withoutUndefined<V5ChartDefinitionSeries>({
    ...series,
    displayName: (can.canLabel && m.displayName) || undefined,
    type: can.canSetSeriesType
      ? m.seriesType ?? getDefaultSeriesType(index)
      : undefined,
    plotOnSeparateAxis: (can.canHaveOwnAxis && m.hasOwnAxis) || undefined,
    opposite: (can.canSetAxisSide && m.isAxisOpposite) || undefined,
    plotAverage: (can.canShowAverageAndTrend && m.showAverage) || undefined,
    plotTrend: (can.canShowAverageAndTrend && m.showTrend) || undefined,
    minY: range.minY,
    maxY: range.maxY,
    minRangePercent: range.minRangePercent,
    cellColourConfig: can.canColourByThresholds
      ? m.cellColourConfig
      : undefined,
  });
};

// Data labels, for the charts that draw them
export const buildDataLabelParts = ({
  options,
  display,
}: {
  options: Studio.ChartOptions;
  display: DisplayCapabilities;
}): Partial<V5ChartDefinition> =>
  display.canShowDataLabels && options.showDataLabels
    ? _.omitBy(
        {
          showDataLabels: true,
          labelStep: display.canStepLabels ? options.labelStep : undefined,
        },
        _.isUndefined,
      )
    : {};

// How a Bullet draws its rows: only what's switched on
const toBulletDisplay = (
  display: BulletCharts.DisplayOptions | undefined,
): BulletCharts.DisplayOptions | undefined => {
  const on = _.pickBy(display ?? {}, (isOn) => isOn === true);
  return _.isEmpty(on) ? undefined : on;
};

// A Bullet's display options, kept on its chartDef so dashboards draw them
export const buildBulletDisplayParts = ({
  options,
  display,
}: {
  options: Studio.ChartOptions;
  display: DisplayCapabilities;
}): Partial<V5ChartDefinition> => {
  const bulletDisplay = display.isBullet
    ? toBulletDisplay(options.bulletDisplay)
    : undefined;
  return bulletDisplay ? { bulletDisplay } : {};
};

export const buildStackedBarOptions = (
  options: Studio.ChartOptions,
): StackedBarOptions => {
  const stack = options.stack ?? {};
  return {
    direction: stack.direction ?? 'vertical',
    asPercentage: !!stack.isPercentage,
    altPivot: !!stack.isPivoted,
  };
};

export const buildStackedAreaOptions = (
  options: Studio.ChartOptions,
): StackedAreaOptions => ({ altPivot: !!(options.stack ?? {}).isPivoted });

// Without undefined values; undefined when nothing is left
const compact = <T extends object>(value: T): T | undefined => {
  const compacted = _.omitBy(value, _.isUndefined);
  return _.isEmpty(compacted) ? undefined : (compacted as T);
};

const SERIES_TYPES: Studio.ChartMetricOptions['seriesType'][] = [
  'column',
  'bar',
  'line',
];

const guessMetricOptions = ({
  series,
  index,
  display,
  gadgetType,
}: {
  series: V5ChartDefinitionSeries;
  index: number;
  display: DisplayCapabilities;
  gadgetType: GadgetType;
}): Studio.ChartMetricOptions | undefined => {
  const hasOwnAxis =
    display.isCombo && index > 0 && !!series.plotOnSeparateAxis;
  const can = getMetricCapabilities({
    display,
    metricOptions: { hasOwnAxis },
    index,
  });
  // A combo series without a type (or a blank one, as the classic editor
  // saved some) is drawn as the chart's columns
  const type = gadgetType === 'combo' ? series.type || 'column' : undefined;
  const seriesType = SERIES_TYPES.find((t) => t === type);
  return compact<Studio.ChartMetricOptions>({
    displayName: (can.canLabel && series.displayName) || undefined,
    seriesType:
      can.canSetSeriesType &&
      seriesType &&
      seriesType !== getDefaultSeriesType(index)
        ? seriesType
        : undefined,
    hasOwnAxis: hasOwnAxis || undefined,
    isAxisOpposite: (can.canSetAxisSide && series.opposite) || undefined,
    showAverage:
      (can.canShowAverageAndTrend && series.plotAverage) || undefined,
    showTrend: (can.canShowAverageAndTrend && series.plotTrend) || undefined,
    minY: can.canSetAxisRange ? series.minY : undefined,
    maxY: can.canSetAxisRange ? series.maxY : undefined,
    minRangePercent: can.canSetAxisRange ? series.minRangePercent : undefined,
    cellColourConfig: can.canColourByThresholds
      ? series.cellColourConfig
      : undefined,
  });
};

const guessStack = ({
  chartDef,
  display,
}: {
  chartDef: V5ChartDefinition;
  display: DisplayCapabilities;
}): Studio.ChartStackOptions | undefined => {
  if (display.stack === 'bar') {
    // Drawn horizontally when saved without options
    const stacked = chartDef.stackedBarOptions ?? {
      direction: 'horizontal',
      asPercentage: false,
      altPivot: false,
    };
    return compact<Studio.ChartStackOptions>({
      direction: stacked.direction === 'horizontal' ? 'horizontal' : undefined,
      isPercentage: stacked.asPercentage || undefined,
      isPivoted: stacked.altPivot || undefined,
    });
  }
  if (display.stack === 'area') {
    return compact<Studio.ChartStackOptions>({
      isPivoted: chartDef.stackedAreaOptions?.altPivot || undefined,
    });
  }
  return undefined;
};

// The display options closest to a chart definition: only those that differ
// from Studio's defaults. Whether they reproduce it is checked separately.
export const guessDisplayOptions = ({
  chartDef,
  content,
}: {
  chartDef: V5ChartDefinition;
  // metricIds: each series' key, in order (see seriesVariants)
  content: Pick<Studio.ChartContent, 'visType' | 'metricIds'> &
    Partial<Pick<Studio.ChartContent, 'chart'>>;
}): Partial<Studio.ChartOptions> => {
  const display = getDisplayCapabilities(content);
  const keys = content.metricIds ?? [];
  const metrics = chartDef.series.reduce<
    Record<string, Studio.ChartMetricOptions>
  >((acc, series, index) => {
    const m = compact<Studio.ChartMetricOptions>({
      ...guessMetricOptions({
        series,
        index,
        display,
        gadgetType: chartDef.gadgetType,
      }),
      bullet: guessBulletOptions({ chartDef, index, keys }),
    });
    return m ? { ...acc, [keys[index] ?? series.metricId]: m } : acc;
  }, {});
  const hasLabels = display.canShowDataLabels && !!chartDef.showDataLabels;
  return _.omitBy(
    {
      showDataLabels: hasLabels || undefined,
      labelStep:
        hasLabels && display.canStepLabels ? chartDef.labelStep : undefined,
      stack: guessStack({ chartDef, display }),
      bulletDisplay: display.isBullet
        ? toBulletDisplay(chartDef.bulletDisplay)
        : undefined,
      metrics: _.isEmpty(metrics) ? undefined : metrics,
    },
    _.isUndefined,
  );
};
